import { motion } from "framer-motion";
import { SafeImage } from "@/components/ui/SafeImage";
import type { ReferenceEntry } from "@/features/references/types";
import { FeaturedReferenceBadge } from "./FeaturedReferenceBadge";
import { RatingDisplay } from "./RatingDisplay";

interface ReferenceCardProps {
  reference: ReferenceEntry;
}

export function ReferenceCard({ reference }: ReferenceCardProps) {
  const subtitle = [reference.roleTitle, reference.organization].filter(Boolean).join(" / ");

  return (
    <motion.article
      whileHover={{ y: -6 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className={
        reference.featured
          ? "group h-full min-w-0 rounded-lg border border-cyan-300/60 bg-black/70 p-4 shadow-neon transition hover:border-cyan-200 sm:p-5"
          : "group h-full min-w-0 rounded-lg border border-cyan-300/20 bg-black/60 p-4 shadow-[0_0_35px_rgba(34,211,238,0.08)] transition hover:border-cyan-300/70 hover:shadow-neon sm:p-5"
      }
    >
      <div className="flex items-start justify-between gap-4">
        <div className="grid size-16 shrink-0 place-items-center overflow-hidden rounded-full border border-cyan-300/25 bg-cyan-300/5">
          {reference.avatarUrl ? (
            <SafeImage
              src={reference.avatarUrl}
              alt={reference.name}
              fallbackLabel={reference.name.slice(0, 2)}
              className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <span className="font-display text-xl font-bold uppercase text-cyan-100/80">
              {reference.name.slice(0, 2)}
            </span>
          )}
        </div>
        {reference.featured ? <FeaturedReferenceBadge /> : null}
      </div>

      <div className="mt-5">
        <RatingDisplay rating={reference.rating} />
      </div>

      <blockquote className="mt-5 break-words text-base leading-7 text-white sm:text-lg sm:leading-8">
        "{reference.quote}"
      </blockquote>

      <div className="mt-6 border-t border-cyan-300/15 pt-4">
        <p className="break-words font-display text-lg font-bold uppercase text-white sm:text-xl">{reference.name}</p>
        {subtitle ? <p className="mt-1 text-sm text-content-secondary">{subtitle}</p> : null}
        {reference.projectType ? (
          <p className="mt-3 w-fit rounded border border-teal-300/30 bg-teal-300/10 px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-teal-100">
            {reference.projectType}
          </p>
        ) : null}
      </div>
    </motion.article>
  );
}
